import { Form, Formik } from "formik";
import type {
  AssetPosition,
  AssetPositionConfigurationInput,
  BasicAuthCredentials,
  GenericCSVConfig,
  PublicKeyCredentials,
  SFTPReceiverParamsInput,
} from "kubb";
import { sftpParserTypeEnum } from "kubb";
import type React from "react";
import * as Yup from "yup";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import { ButtonGroupField } from "../../../components/Forms/ButtonGroup";
import NewTextFormComponent from "../../../components/Forms/NewTextFormComponent";
import { SelectComboField } from "../../../components/Forms/SelectCombo/SelectCombo";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { Button } from "../../../components/Theme/button";
import { Heading } from "../../../components/Theme/heading";
import DisplayIfUserType from "../../../components/utils/DisplayIfUserType/DisplayIfUserType";
import { useUpdateAssetPosition } from "../../../hooks/updateEntity";
import useUserHasRoles from "../../../hooks/user/getCurrentUserRole";
import GenericCSVConfigEditor from "./GenericCSVConfigEditor";
import {
  NONE_PARSER_TYPE,
  PARSER_TYPE_OPTIONS,
} from "./SFTPReceiverIntegrationForm";

interface SFTPCredentialsDisplayProps {
  assetPosition: AssetPosition;
  refresh?: () => void;
}

interface GenericCSVConfigFormValues {
  columns: {
    timestampColumn: string;
    identifierColumn: string;
    valueColumn: string;
  };
  timestamp: {
    format: string;
    timezone: string;
  };
}

interface FormValues {
  sftpServer: string;
  serverFingerprints: string;
  username: string;
  authenticationType: "public_key" | "basic_auth";
  password: string;
  publicKey: string;
  parserType: string;
  genericCsvConfig: GenericCSVConfigFormValues;
}

type SFTPCredentials = PublicKeyCredentials | BasicAuthCredentials;

const genericCsvConfigSchema = Yup.object().shape({
  columns: Yup.object()
    .shape({
      timestampColumn: Yup.string().min(1, "Required").required("Required"),
      identifierColumn: Yup.string().min(1, "Required").required("Required"),
      valueColumn: Yup.string().min(1, "Required").required("Required"),
    })
    .test("distinct-columns", "Column names must be distinct", (cols) => {
      if (!cols) return true;
      const values = [
        cols.timestampColumn,
        cols.identifierColumn,
        cols.valueColumn,
      ].filter(Boolean);
      return new Set(values).size === values.length;
    }),
  timestamp: Yup.object().shape({
    timezone: Yup.string().required("Timezone is required"),
  }),
});

const validationSchema = Yup.lazy((values: FormValues) => {
  if (values.parserType === sftpParserTypeEnum.generic_csv) {
    return Yup.object().shape({
      genericCsvConfig: genericCsvConfigSchema,
    });
  }
  return Yup.object().shape({});
});

function getSFTPCredentials(
  assetPosition: AssetPosition,
): SFTPCredentials | undefined {
  const integration = assetPosition.configuration?.integration;
  if (
    integration &&
    "sftp_credentials" in integration &&
    integration.sftp_credentials
  ) {
    return integration.sftp_credentials as SFTPCredentials;
  }
  return undefined;
}

function getParserType(assetPosition: AssetPosition): string {
  const integration = assetPosition.configuration
    ?.integration as SFTPReceiverParamsInput | null;
  return integration?.parser_type || NONE_PARSER_TYPE;
}

function getGenericCsvConfigFormValues(
  assetPosition: AssetPosition,
): GenericCSVConfigFormValues {
  const integration = assetPosition.configuration
    ?.integration as SFTPReceiverParamsInput | null;
  const config = integration?.generic_csv_config as
    | GenericCSVConfig
    | null
    | undefined;

  if (!config) {
    return {
      columns: { timestampColumn: "", identifierColumn: "", valueColumn: "" },
      timestamp: { format: "", timezone: "UTC" },
    };
  }

  return {
    columns: {
      timestampColumn: config.columns?.timestamp_column || "",
      identifierColumn: config.columns?.identifier_column || "",
      valueColumn: config.columns?.value_column || "",
    },
    timestamp: {
      format: config.timestamp?.format || "",
      timezone: config.timestamp?.timezone || "UTC",
    },
  };
}

function formValuesToApiConfig(
  config: GenericCSVConfigFormValues,
): GenericCSVConfig {
  return {
    columns: {
      timestamp_column: config.columns.timestampColumn,
      identifier_column: config.columns.identifierColumn,
      value_column: config.columns.valueColumn,
    },
    timestamp: {
      format: config.timestamp.format || null,
      timezone: config.timestamp.timezone || "UTC",
    },
  };
}

export default function SFTPCredentialsDisplay(
  props: SFTPCredentialsDisplayProps,
): React.ReactElement {
  const { assetPosition, refresh } = props;
  const canEdit = useUserHasRoles(["editor", "admin", "superadmin"]);
  const { update, loading, error } = useUpdateAssetPosition(assetPosition);

  const credentials = getSFTPCredentials(assetPosition);
  const authType = credentials?.type || "public_key";
  const integrationType =
    assetPosition.configuration?.integration?.integration_type;
  const isSftpReceiver = integrationType === "sftp_receiver";

  const fingerprints = [
    import.meta.env.VITE_SFTP_FINGERPRINT_RSA &&
      `RSA: ${import.meta.env.VITE_SFTP_FINGERPRINT_RSA}`,
    import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA &&
      `ECDSA: ${import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA}`,
  ]
    .filter(Boolean)
    .join("\n");

  const initialValues: FormValues = {
    sftpServer: import.meta.env.VITE_SFTP_SERVER || "",
    serverFingerprints: fingerprints,
    username: credentials?.username || assetPosition.asset_position_id || "",
    authenticationType: authType as "public_key" | "basic_auth",
    password:
      authType === "basic_auth"
        ? (credentials as BasicAuthCredentials)?.password || ""
        : "",
    publicKey:
      authType === "public_key"
        ? (credentials as PublicKeyCredentials)?.public_key || ""
        : "",
    parserType: getParserType(assetPosition),
    genericCsvConfig: getGenericCsvConfigFormValues(assetPosition),
  };

  const handleSubmit = (
    values: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    setSubmitting(true);

    const newCredentials: SFTPCredentials =
      values.authenticationType === "basic_auth"
        ? ({
            type: "basic_auth",
            password: values.password,
          } as BasicAuthCredentials)
        : ({
            type: "public_key",
            public_key: values.publicKey,
          } as PublicKeyCredentials);

    const isGenericCsv = values.parserType === sftpParserTypeEnum.generic_csv;

    const newConfiguration = {
      ...assetPosition.configuration,
      integration: {
        ...assetPosition.configuration?.integration,
        sftp_credentials: newCredentials,
        ...(isSftpReceiver && {
          parser_type:
            values.parserType === NONE_PARSER_TYPE ? null : values.parserType,
          generic_csv_config: isGenericCsv
            ? formValuesToApiConfig(values.genericCsvConfig)
            : null,
        }),
      },
    } as AssetPositionConfigurationInput;

    update({
      ...assetPosition,
      configuration: newConfiguration,
    })
      .then(() => {
        refresh?.();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <Heading>SFTP Credentials</Heading>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ values, isSubmitting, dirty }) => {
          const isBasicAuth = values.authenticationType === "basic_auth";
          const isGenericCsv =
            values.parserType === sftpParserTypeEnum.generic_csv;

          return (
            <Form>
              <BlockSpinner loading={loading}>
                {/* Parser type selector - only for sftp_receiver and editors */}
                {isSftpReceiver && (
                  <DisplayIfUserType userTypes="editor">
                    <SelectComboField
                      fieldName="parserType"
                      label={{ label: "Parser Type" }}
                      options={PARSER_TYPE_OPTIONS}
                    />
                  </DisplayIfUserType>
                )}

                {/* Generic CSV config editor */}
                {isSftpReceiver && isGenericCsv && (
                  <GenericCSVConfigEditor
                    namespace="genericCsvConfig"
                    disabled={!canEdit}
                  />
                )}

                {/* Auth type switcher - only for editors */}
                <DisplayIfUserType userTypes="editor">
                  <ButtonGroupField
                    fieldName="authenticationType"
                    label={{ label: "Authentication Type" }}
                    options={[
                      { value: "public_key", label: "SSH Key" },
                      { value: "basic_auth", label: "Password" },
                    ]}
                  />
                </DisplayIfUserType>

                {/* SFTP Server - always read-only */}
                <NewTextFormComponent
                  fieldName="sftpServer"
                  label={{ label: "SFTP Server" }}
                  staticValue
                  disabled
                  clipboard
                />

                {/* Server fingerprints - read-only reference for verification */}
                {(import.meta.env.VITE_SFTP_FINGERPRINT_RSA ||
                  import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA) && (
                  <NewTextFormComponent
                    fieldName="serverFingerprints"
                    label={{ label: "Server Fingerprints" }}
                    staticValue
                    disabled
                    clipboard
                  />
                )}

                {/* Username - always read-only (set by backend) */}
                <NewTextFormComponent
                  fieldName="username"
                  label={{ label: "Username" }}
                  staticValue
                  disabled
                  clipboard
                  autoComplete="off"
                />

                {/* Credentials - read-only for viewers, editable for editors */}
                {isBasicAuth ? (
                  canEdit ? (
                    <NewTextFormComponent
                      fieldName="password"
                      label={{ label: "Password" }}
                      type="password"
                      autoComplete="off"
                    />
                  ) : (
                    <NewTextFormComponent
                      fieldName="password"
                      label={{ label: "Password" }}
                      staticValue
                      disabled
                      clipboard
                      autoComplete="off"
                    />
                  )
                ) : canEdit ? (
                  <NewTextFormComponent
                    fieldName="publicKey"
                    label={{ label: "SSH Public Key" }}
                    type="textarea"
                    autoComplete="off"
                  />
                ) : (
                  <NewTextFormComponent
                    fieldName="publicKey"
                    label={{ label: "SSH Public Key" }}
                    staticValue
                    disabled
                    clipboard
                    autoComplete="off"
                  />
                )}

                {/* Submit button - only for editors */}
                <DisplayIfUserType userTypes="editor">
                  <Button
                    type="submit"
                    color="brandLight"
                    disabled={isSubmitting || !dirty}
                  >
                    Update Credentials
                  </Button>
                </DisplayIfUserType>

                <ErrorBanner error={error} />
              </BlockSpinner>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
